import _ from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";
import useFetchInterval from "src/ui/hooks/useFetchInterval";
import useTableFetchLoading from "src/ui/hooks/useTableFetchLoading";
import { CollapsibleRow, CollapsibleRows, CollapsibleSummary } from "src/ui/shared/table/CollapsibleRow";
import { FilterHeader } from "src/ui/shared/table/headers/FilterHeader";
import { MinimalTable, MinimalTableCell, MinimalTableHeader } from "src/ui/shared/table/MinimalTableComponents";
import { CustomTableHeader } from "src/ui/shared/table/TableHeader";

import { useAllCurrentWorkspaceFiles } from "@dashboard/files/api/helpers";
import { GetFilesParams } from "@dashboard/files/api/rtkClient";
import { SourceType } from "@dashboard/files/types";
import { FileExplorerHeader } from "@dashboard/shared/components/SidePanel/shared/FileExplorerHeader";
import { PaneElement } from "@dashboard/shared/components/SidePanel/shared/PaneElement";
import { TagFilter } from "@dashboard/shared/components/TagFilter";
import { Device } from "@devices/types";
import { FileInfoWithVersions, hasAnalyzerInProgress, normalizeFiles } from "@files/utils";
import { TableRow } from "@mui/material";
import { TableBody } from "@shared/table/TableBody";
import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";

import ActionsCell from "./ActionsCell";
import { columns, FileNameCell, FileTabTagsCell, SourceCell, useParams } from "./FileTab.utils";

export type LicenseFileTabParams = Required<
    Pick<GetFilesParams, "workspaceId" | "sortCriteria" | "sortDirection" | "groups" | "sourceTypes" | "includeParentFiles">
>;

interface Props {
    device: Device;
    groupId: number;
}
const FileTab = (props: Props) => {
    const [params, setParams] = useParams(props.groupId);

    const { t } = useTranslation(["sidepanels", "common"]);

    const {
        data: files,
        isFetching,
        isLoading,
        isError,
        refetch,
    } = useAllCurrentWorkspaceFiles(params, { preferCacheValue: false });
    const data = React.useMemo(() => normalizeFiles(files), [files]);
    const tableLoading = useTableFetchLoading(isFetching, isLoading, params);

    const table = useReactTable<FileInfoWithVersions>({
        data,
        columns,
        meta: { device: props.device, selectedGroupId: props.groupId },
        getCoreRowModel: getCoreRowModel(),
    });

    const onSort = (id: string) => {
        setParams((prev) => ({
            ...prev,
            sortCriteria: id,
            sortDirection: prev.sortDirection === "asc" ? "desc" : "asc",
        }));
    };
    const onSourceTypesFilter = (_values: Record<string, boolean>) => {
        const arr = Object.keys(_.pickBy(_values, (item) => item));
        const value = arr.map((item) => (item === "device" ? SourceType.Device : SourceType.User));
        setParams((prev) => ({ ...prev, sourceTypes: value }));
    };

    const onIncludeParentFilesChange = (includeParentFiles: boolean) => {
        setParams((prev) => ({ ...prev, includeParentFiles }));
    };

    const handleResetFilter = () => {
        setParams((prev) => ({ ...prev, sourceTypes: [] }));
    };

    useFetchInterval(refetch, {
        condition: hasAnalyzerInProgress(files),
    });

    return (
        <PaneElement.Container>
            <PaneElement.Header>{t("sidepanels:devicePanel.filesTab.availableFiles")}</PaneElement.Header>
            <FileExplorerHeader
                selectedGroupId={props.groupId}
                includeParentFiles={params.includeParentFiles}
                onIncludeParentFilesChange={onIncludeParentFilesChange}
            />
            <PaneElement.TableLayout contentDeps={[table.getRowModel().rows.length]}>
                <MinimalTable>
                        <MinimalTableHeader>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <CustomTableHeader
                                            key={header.id}
                                            sortCriteria={params.sortCriteria}
                                            sortDirection={params.sortDirection}
                                            onSort={onSort}
                                            {...header}
                                        >
                                            {/* TODO: fill out TagFilter after the tag filtering is implemented on the backend. */}
                                            {header.id === "filesTags" ? (
                                                <TagFilter tags={[]} filters={[]} onChange={() => {}} />
                                            ) : null}

                                            {header.id === "source" ? (
                                                <FilterHeader
                                                    title={t("sidepanels:devicePanel.table.filter.source")}
                                                    sortDisabled
                                                    onSubmit={onSourceTypesFilter}
                                                    initialValues={{
                                                        user: params.sourceTypes.includes(SourceType.User),
                                                        device: params.sourceTypes.includes(SourceType.Device),
                                                    }}
                                                    options={[
                                                        { key: "user", label: t("sidepanels:devicePanel.options.user") },
                                                        { key: "device", label: t("sidepanels:devicePanel.options.device") },
                                                    ]}
                                                />
                                            ) : null}
                                        </CustomTableHeader>
                                    ))}
                                </TableRow>
                            ))}
                        </MinimalTableHeader>
                        <TableBody<FileInfoWithVersions>
                            table={table}
                            filter={{ isActive: !_.isEmpty(params.sourceTypes), onResetFilter: handleResetFilter }}
                            noDataMessage={t("sidepanels:devicePanel.table.noFiles")}
                            error={{ isError, onError: refetch }}
                            loading={tableLoading}
                        >
                            {({ rows }) =>
                                rows.map((row) => (
                                    <CollapsibleRow key={row.id}>
                                        <CollapsibleSummary disabled={row.original.olderVersions.length === 0} row={row}>
                                            {row.getVisibleCells().map((cell) => (
                                                <React.Fragment key={cell.id}>
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </React.Fragment>
                                            ))}
                                        </CollapsibleSummary>
                                        <CollapsibleRows>
                                            {row.original.olderVersions.map((rowItem) => (
                                                <TableRow key={rowItem.id}>
                                                    <MinimalTableCell />
                                                    <FileNameCell file={rowItem} />
                                                    <SourceCell file={rowItem} />
                                                    <FileTabTagsCell file={rowItem} />
                                                    <MinimalTableCell style={{ overflow: "visible" }}>
                                                        <ActionsCell file={rowItem} />
                                                    </MinimalTableCell>
                                                </TableRow>
                                            ))}
                                        </CollapsibleRows>
                                    </CollapsibleRow>
                                ))
                            }
                        </TableBody>
                    </MinimalTable>
            </PaneElement.TableLayout>
        </PaneElement.Container>
    );
};

export default FileTab;
